import { useEffect, useRef } from 'react';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  pulsePhase: number;
}

interface Connection {
  from: number;
  to: number;
  progress: number;
  speed: number;
  active: boolean;
  packet: number;
}

export default function BlockchainCanvas({ className = '' }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const connectionsRef = useRef<Connection[]>([]);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      init();
    };

    const init = () => {
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      const count = Math.min(22, Math.floor((W * H) / 6000));

      nodesRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: 2 + Math.random() * 3,
        opacity: 0.4 + Math.random() * 0.5,
        pulsePhase: Math.random() * Math.PI * 2,
      }));

      const conns: Connection[] = [];
      for (let i = 0; i < count; i++) {
        for (let j = i + 1; j < count; j++) {
          const n1 = nodesRef.current[i];
          const n2 = nodesRef.current[j];
          const dist = Math.hypot(n1.x - n2.x, n1.y - n2.y);
          if (dist < W * 0.28) {
            conns.push({
              from: i,
              to: j,
              progress: Math.random(),
              speed: 0.003 + Math.random() * 0.005,
              active: Math.random() > 0.4,
              packet: 0,
            });
          }
        }
      }
      connectionsRef.current = conns.slice(0, 60);
    };

    const draw = () => {
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      timeRef.current += 0.016;

      ctx.clearRect(0, 0, W, H);

      const nodes = nodesRef.current;
      const conns = connectionsRef.current;

      // Update nodes
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });

      // Draw connections
      conns.forEach((c) => {
        if (!c.active) return;
        const a = nodes[c.from];
        const b = nodes[c.to];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (dist > W * 0.32) return;
        const alpha = Math.max(0, 1 - dist / (W * 0.28));

        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = `rgba(255, 127, 10, ${alpha * 0.18})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();

        // Animate data packet
        c.progress += c.speed;
        if (c.progress > 1) c.progress = 0;
        const px = a.x + (b.x - a.x) * c.progress;
        const py = a.y + (b.y - a.y) * c.progress;
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 127, 10, ${alpha * 0.7})`;
        ctx.fill();
      });

      // Draw nodes
      nodes.forEach((n, i) => {
        const pulse = Math.sin(timeRef.current * 1.5 + n.pulsePhase) * 0.3 + 0.7;
        const r = n.radius * pulse;

        // Glow
        const grd = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 3);
        grd.addColorStop(0, `rgba(255, 127, 10, ${n.opacity * 0.35})`);
        grd.addColorStop(1, 'rgba(255, 127, 10, 0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 3, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = i % 4 === 0 ? `rgba(255, 127, 10, ${n.opacity})` : `rgba(30, 41, 59, ${n.opacity * 0.6})`;
        ctx.fill();
      });

      frameRef.current = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={`w-full h-full ${className}`} style={{ display: 'block' }} />;
}
